/* eslint-disable jsx-a11y/no-redundant-roles */
import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../authContext';
import { useCompany } from '../../services/CompanyContext';
import { useUser } from '../../services/UserContext';
import { prefetchRoute } from '../../routePrefetch';
import { isGodMode, endGodMode } from '../../godMode/session';
import { filterNavigation } from './godModeNav';

import ChatbotWidget from './ChatbotWidget';
import {
  Bars3Icon,
  Cog6ToothIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  DocumentCheckIcon,
  ComputerDesktopIcon,
  InformationCircleIcon,
  ChartBarSquareIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Scale } from 'lucide-react';

const fullSidebarConfig = [
  { name: 'Tableau de bord', href: '/dashboard', icon: HomeIcon },
  {
    name: 'Enquêtes',
    icon: DocumentCheckIcon,
    children: [
      { name: 'Mes enquêtes', href: '/surveys/mine' },
      { name: 'Créer une enquête', href: '/surveys/create' },
      { name: 'Modèles', href: '/surveys/templates' },
    ],
  },
  {
    name: 'Résultats',
    icon: ChartBarSquareIcon,
    children: [
      { name: "Vue d'ensemble", href: '/results/overview' },
      { name: 'Insights IA', href: '/results/insights' },
      { name: 'Points critiques', href: '/results/critical-points' },
      { name: "Plan d'action", href: '/results/action-plan' },
      { name: 'Analyse détaillée', href: '/results/analysis' },
    ],
  },
  {
    name: 'Organisation',
    icon: UsersIcon,
    children: [
      { name: 'Collaborateurs', href: '/organization/employees' },
      { name: 'Équipes', href: '/organization/teams' },
      { name: 'Managers', href: '/organization/managers' },
    ],
  },
  ...(process.env.REACT_APP_ENABLE_BENCHMARKS === 'true' ? [{
    name: 'Benchmark', href: '/benchmark', icon: Scale,
  }] : []),
  {
    name: 'Paramètres',
    icon: Cog6ToothIcon,
    children: [
      { name: 'Compte', href: '/settings/account' },
      { name: 'Entreprise', href: '/settings/company' },
      { name: 'Facturation', href: '/settings/billing' },
      { name: 'Utilisateurs', href: '/settings/users' },
    ],
  },
];

const godModeNavigation = [
  { name: 'Revenir au back-office', href: '#' },
];

const userNavigation = [
  { name: 'Mon compte', href: '/settings/account' },
  { name: 'Déconnexion', href: '#' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function SidebarNav({ onNavigate }) {
  const location = useLocation();
  const [openSections, setOpenSections] = useState({});
  // Mode intervention : les entrées qui mènent à un écran masqué disparaissent.
  const sidebarConfig = useMemo(() => filterNavigation(fullSidebarConfig, isGodMode()), []);

  // Auto-open sections that match current path
  useEffect(() => {
    const newOpen = {};
    sidebarConfig.forEach(item => {
      if (item.children) {
        const isChildActive = item.children.some(child =>
          location.pathname.startsWith(child.href)
        );
        if (isChildActive) newOpen[item.name] = true;
      }
    });
    setOpenSections(prev => ({ ...prev, ...newOpen }));
  }, [location.pathname, sidebarConfig]);

  const toggleSection = (name) => {
    setOpenSections(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const isChildActive = (item) => {
    if (!item.children) return false;
    return item.children.some(child => location.pathname.startsWith(child.href));
  };

  return (
    <ul role="list" className="space-y-1">
      {sidebarConfig.map((item) => (
        <li key={item.name}>
          {item.children ? (
            <>
              <button
                onClick={() => toggleSection(item.name)}
                className={classNames(
                  isChildActive(item) ? 'text-bsrate font-bold' : 'text-black',
                  'group flex w-full items-center gap-x-3 rounded-md p-1 pl-2 text-sm leading-6 font-semibold hover:bg-bsrate hover:text-white transition'
                )}
              >
                <div className="flex w-5 h-5 items-center mt-0.5">
                  <item.icon className="h-5 w-5 shrink-0 text-bsrate group-hover:text-white" aria-hidden="true" />
                </div>
                <span className="min-w-0 flex-1 truncate text-left">{item.name}</span>
                <ChevronRightIcon
                  className={classNames(
                    openSections[item.name] ? 'rotate-90' : '',
                    'h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:text-white'
                  )}
                  aria-hidden="true"
                />
              </button>
              {openSections[item.name] && (
                <ul className="mt-1 space-y-0.5 pl-7">
                  {item.children.map(child => (
                    <li key={child.href}>
                      <NavLink
                        to={child.href}
                        onClick={onNavigate}
                        onMouseEnter={() => prefetchRoute(child.href)}
                        onFocus={() => prefetchRoute(child.href)}
                        className={({ isActive }) =>
                          classNames(
                            isActive ? 'bg-bsrate text-white' : 'text-slate-700 hover:bg-bsrate hover:text-white',
                            'block rounded-md py-1 px-3 text-sm font-medium transition'
                          )
                        }
                      >
                        {child.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <NavLink
              to={item.href}
              onClick={onNavigate}
              onMouseEnter={() => prefetchRoute(item.href)}
              onFocus={() => prefetchRoute(item.href)}
              className={({ isActive }) =>
                classNames(
                  isActive ? 'bg-bsrate text-white' : 'text-black hover:bg-bsrate hover:text-white',
                  'group flex gap-x-3 rounded-md p-1 pl-2 text-sm leading-6 font-semibold transition'
                )
              }
            >
              <div className="flex w-5 h-5 items-center mt-0.5">
                <item.icon className="h-5 w-5 shrink-0 text-bsrate group-hover:text-white" aria-hidden="true" />
              </div>
              {item.name}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );
}

function Sidebar3({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Mode intervention : pas d'aide, de chatbot ni de « Mon compte » (le compte
  // et le mot de passe de l'admin emprunté ne se modifient jamais), et le menu
  // utilisateur devient « Revenir au back-office ».
  const godMode = isGodMode();
  const { signOut } = useAuth();
  const { companyName, companyLogo } = useCompany();
  const { user } = useUser();
  const navigate = useNavigate();

  const handleSignOut = () => {
    signOut();
    navigate('/signin');
    window.location.reload();
  };

  return (
    <>
      <div>
        {/* Mobile sidebar */}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className={`relative ${godMode ? "z-[70]" : "z-50"} lg:hidden`} onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                        <span className="sr-only">Fermer le menu</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-bsrate2 px-6 pb-4">
                    <div className="flex w-full flex-col items-center pt-4">
                      <img
                        className="h-16 w-16 object-cover rounded-full border border-bsrate"
                        src={companyLogo || '/images/default-company.png'}
                        alt={companyName}
                      />
                      <div className="mt-2 break-words px-2 text-center text-sm font-bold uppercase leading-tight text-bsrate">{companyName}</div>
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <SidebarNav onNavigate={() => setSidebarOpen(false)} />
                      <div className="mt-6 space-y-1 px-1">
                        <a
                          href="https://backstagerate.com"
                          target="_blank"
                          rel="noreferrer"
                          className="group flex gap-x-3 rounded-md p-1 pl-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-bsrate hover:text-white"
                        >
                          <ComputerDesktopIcon className="h-5 w-5 mt-0.5 shrink-0 text-bsrate group-hover:text-white" aria-hidden="true" />
                          Retour au site
                        </a>
                        {!godMode && (<a
                          href="/help"
                          className="group flex gap-x-3 rounded-md p-1 pl-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-bsrate hover:text-white"
                        >
                          <InformationCircleIcon className="h-5 w-5 mt-0.5 shrink-0 text-bsrate group-hover:text-white" aria-hidden="true" />
                          Besoin d'aide ?
                        </a>

                        )}
                      </div>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Desktop sidebar */}
        {/* En mode intervention, le bandeau (2,5 rem, collé en haut) recouvrirait le haut de la barre fixe : elle se décale d'autant. */}
        <div className="hidden w-1/6 lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col" style={godMode ? { top: '2.5rem' } : undefined}>
          <div className="flex flex-col border-bsrate border-r h-full bg-bsrate2 pb-4">
            <div className="flex flex-col px-4 mb-4 items-center">
              <img
                className="h-24 w-24 object-cover rounded-full mt-4 mb-3 border border-bsrate"
                src={companyLogo || '/images/default-company.png'}
                alt={companyName}
              />
              <div className="break-words px-2 text-center text-sm font-bold uppercase leading-tight text-bsrate">{companyName}</div>
            </div>
            <nav className="flex flex-1 flex-col overflow-y-auto px-4 mx-1 mt-2">
              <SidebarNav />
              <div className="mt-6 space-y-1 px-3">
                <a
                  href="https://backstagerate.com"
                  target="_blank"
                  rel="noreferrer"
                  className="group flex gap-x-3 rounded-md p-1 text-sm font-semibold leading-6 text-gray-700 hover:bg-bsrate hover:text-white pl-2"
                >
                  <ComputerDesktopIcon className="h-5 w-5 mt-0.5 shrink-0 text-bsrate group-hover:text-white" aria-hidden="true" />
                  Retour au site
                </a>
                {!godMode && (<a
                  href="/help"
                  className="group flex gap-x-3 rounded-md p-1 text-sm font-semibold leading-6 text-gray-700 hover:bg-bsrate hover:text-white pl-2"
                >
                  <InformationCircleIcon className="h-5 w-5 mt-0.5 shrink-0 text-bsrate group-hover:text-white" aria-hidden="true" />
                  Besoin d'aide ?
                </a>

                )}
              </div>
            </nav>
            <div className="flex w-full justify-center">
              <img src="/images/bsr-logo.png" alt="BSRate Logo" className="w-44 mt-4 mb-0 -ml-2" />
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="w-full lg:w-5/6 float-end justify-between">
          <div className="flex shrink-0 items-center justify-between gap-x-4 bg-bsrate2 px-4 py-2 sm:gap-x-6 sm:px-6 lg:px-4">
            <button type="button" className="-m-2.5 p-2.5 text-gray-700 lg:hidden" onClick={() => setSidebarOpen(true)}>
              <span className="sr-only">Ouvrir le menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="ml-auto flex items-center gap-x-4 self-stretch lg:gap-x-6">
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center gap-1 rounded-lg p-1.5 transition-colors hover:bg-base-200/60">
                    <span className="sr-only">Ouvrir le menu utilisateur</span>
                    <img
                      className="h-6 w-6 rounded-full bg-gray-50 object-cover"
                      src={user.image || '/images/default-pp.png'}
                      alt="profile"
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span className="ml-2 max-w-[12rem] truncate text-sm font-medium text-base-content transition-colors group-hover:text-bsrate">{`${user.firstName} ${user.lastName}`}</span>
                      <ChevronDownIcon className="ml-1 h-5 w-5 text-slate-300" aria-hidden="true" />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 w-max min-w-[8rem] origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      {(godMode ? godModeNavigation : userNavigation).map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            item.name === 'Déconnexion' || item.name === 'Revenir au back-office' ? (
                              <button
                                onClick={item.name === 'Déconnexion' ? handleSignOut : endGodMode}
                                className={classNames(
                                  active ? 'bg-gray-50' : '',
                                  'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900'
                                )}
                              >
                                {item.name}
                              </button>
                            ) : (
                              <a
                                href={item.href}
                                className={classNames(
                                  active ? 'bg-gray-50' : '',
                                  'block px-3 py-1 text-sm leading-6 text-gray-900'
                                )}
                              >
                                {item.name}
                              </a>
                            )
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <main className="py-1 px-3 bg-bsrate2 min-h-screen">
            <div className="px-0 sm:px-6 lg:px-4 pb-8">
              <div className="flex-1 pt-4">{children}</div>
            </div>
          </main>
        </div>
      </div>
      {!godMode && <ChatbotWidget />}
    </>
  );
}

export default Sidebar3;
